import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Plus, Mail, Building2, Banknote } from "lucide-react";

import { useClaimChildren, type ClaimRow } from "@/admin/hooks/use-claims";
import { usePermissions } from "@/admin/hooks/use-permissions";
import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import { Button } from "@/shared/components/ui/button";
import { StatusBadge } from "@/admin/components/shared/status-badge";
import { CreateChildClaimDialog } from "./create-child-claim-dialog";
import { BulkEmailDialog } from "./bulk-email-dialog";
import {
  BulkSettleDialog,
  type BulkSettleClaim,
} from "./bulk-settle-dialog";

/**
 * Unit Claims tab on an HOA parent claim detail page.
 *
 * Shows a summary row + a DataTable of child unit claims, with actions to
 * add a single unit claim or email every unit contact at once. Each row
 * links to the child claim's own detail page.
 *
 * Mounted only when the parent claim's schema key is "hoa" (gated by the
 * detail page) - every label/colour comes from the claim row's
 * backend-supplied ``claim_state`` / ``claim_state_color``.
 */
export function ChildClaimsTab({
  parentUuid,
  defaultPayoutMode = "combined",
  combinedPayee = "",
}: {
  parentUuid: string;
  /** Default payout mode from the parent's intake settlement preference. */
  defaultPayoutMode?: "combined" | "per_claim";
  /** Default combined payee (e.g. the community name). */
  combinedPayee?: string;
}) {
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Bulk settle records and processes payments, so it needs the same
  // capability as recording one (WPS-175 #11); the server refuses without it.
  const canSettle = can("claims.pay_claim");
  const { data: children = [], isLoading } = useClaimChildren(parentUuid);
  const [addOpen, setAddOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);

  const settleClaims: BulkSettleClaim[] = useMemo(
    () =>
      children.map((c) => ({
        uuid: c.uuid,
        label: c.product_name || c.consumer_account_address || "Unit claim",
        ownerName: c.consumer_account_name || undefined,
      })),
    [children],
  );

  const { approvedCount, pendingCount } = useMemo(() => {
    let approved = 0;
    let pending = 0;
    for (const c of children) {
      const state = (c.claim_state ?? "").toLowerCase();
      if (state.includes("approv") || state.includes("settle") || state.includes("paid") || state.includes("compensat")) {
        approved += 1;
      } else if (!state.includes("clos") && !state.includes("deni")) {
        pending += 1;
      }
    }
    return { approvedCount: approved, pendingCount: pending };
  }, [children]);

  const columns: DataTableColumn<ClaimRow>[] = [
    {
      id: "unit",
      header: "Unit / Address",
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-[13px] text-[var(--ink)]">
            {row.product_name || "—"}
          </span>
          {row.consumer_account_address && (
            <span className="text-[11px] text-[var(--ink-3)]">
              {row.consumer_account_address}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "owner",
      header: "Homeowner",
      cell: (row) => (
        <span className="text-[13px] text-[var(--ink-2)]">
          {row.consumer_account_name || "—"}
        </span>
      ),
    },
    {
      id: "state",
      header: "State",
      cell: (row) =>
        row.claim_state ? (
          <StatusBadge status={row.claim_state} color={row.claim_state_color} />
        ) : (
          <span className="text-[12px] text-[var(--ink-3)]">{"—"}</span>
        ),
    },
    {
      id: "assignee",
      header: "Assignee",
      cell: (row) => (
        <span className="text-[12px] text-[var(--ink-2)]">
          {row.assigned_to?.name || "Unassigned"}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Summary + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-[12px] text-[var(--ink-2)]">
          <span className="flex items-center gap-1.5">
            <Building2 size={14} className="text-[var(--ink-3)]" />
            {children.length} unit claim{children.length !== 1 ? "s" : ""}
          </span>
          <span className="text-[var(--ink-3)]">
            {approvedCount} approved · {pendingCount} pending
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEmailOpen(true)}
            disabled={children.length === 0}
            className="cursor-pointer gap-1.5"
          >
            <Mail size={14} />
            Email Contacts
          </Button>
          {canSettle && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSettleOpen(true)}
              disabled={children.length === 0}
              className="cursor-pointer gap-1.5"
            >
              <Banknote size={14} />
              Bulk Settle
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => setAddOpen(true)}
            className="cursor-pointer gap-1.5"
          >
            <Plus size={14} />
            Add Unit Claim
          </Button>
        </div>
      </div>

      <DataTable
        tableId="hoa-child-claims"
        columns={columns}
        data={children}
        isLoading={isLoading}
        rowKey="uuid"
        onRowClick={(row) => navigate(`/claims/${row.uuid}`)}
        emptyMessage="No unit claims yet. Add the first one to get started."
      />

      <CreateChildClaimDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        parentUuid={parentUuid}
      />
      <BulkEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        parentUuid={parentUuid}
        recipientCount={children.length}
      />
      {canSettle && (
        <BulkSettleDialog
          open={settleOpen}
          onOpenChange={setSettleOpen}
          parentUuid={parentUuid}
          claims={settleClaims}
          defaultPayoutMode={defaultPayoutMode}
          defaultCombinedPayee={combinedPayee}
        />
      )}
    </div>
  );
}
